/**
 * AlertsPage — dedicated /alerts route showing all operational alerts.
 * Single-open accordion. "Tell me more" maximizes into a full chat view
 * that replaces the alert list (alert details become first message).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronDown, ChevronUp, Eye, X, MessageSquare, ArrowLeft } from 'lucide-react';
import { useAlerts } from '../../hooks/useAlerts';
import { SectionHeading, Card, EmptyState } from '../ui';
import { ICON_PX } from '../ui/lib/iconSizes';
import { SEVERITY_STYLES } from './alertStyles';
import { AlertAgentChat } from './AlertAgentChat';

// ─── Alert Card (accordion item) ──────────────────────────────────────────────

function AlertCard({ alert, expanded, onToggle, onAcknowledge, onResolve, onDismiss, onTellMeMore, highlighted }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  const sev = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.medium;
  const isEnriched = !!alert.why;
  const impacted = alert.impacted ?? [];
  const actions = alert.actions ?? [];
  const alertId = alert.id ?? alert.alertId;

  return (
    <Card ref={cardRef} className={`p-4 min-h-[80px] ${highlighted ? 'ring-1 ring-aqua shadow-sm' : ''}`}>
      {/* Header — entire row is clickable */}
      <div
        className="flex items-start gap-3 cursor-pointer select-none"
        onClick={() => onToggle(alertId)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(alertId); } }}
      >
        <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${sev.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${sev.badge}`}>{sev.label}</span>
            <span className="text-xs text-text-muted">{alert.triggerEventType}</span>
            {alert.confidence != null && (
              <span className="text-[10px] text-text-muted">conf: {Math.round(alert.confidence * 100)}%</span>
            )}
          </div>
          <p className="text-sm font-medium mt-1 truncate">{alert.summary}</p>
        </div>
        <span className="text-text-muted shrink-0 mt-0.5">
          {expanded ? <ChevronUp size={ICON_PX.md} /> : <ChevronDown size={ICON_PX.md} />}
        </span>
      </div>

      {/* Expanded: Why + Impact + Actions */}
      {expanded && (
        <div className="mt-3 ml-5 space-y-3">
          {isEnriched ? (
            <div className="bg-surface rounded-card p-3 border border-nn-border/50">
              <p className="text-xs font-medium text-text-muted mb-1">Why</p>
              <p className="text-xs leading-relaxed">{alert.why}</p>
            </div>
          ) : (
            <p className="text-xs text-text-muted italic">Enrichment pending — analysis in progress...</p>
          )}

          {impacted.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-muted mb-1">Impact</p>
              <div className="space-y-1">
                {impacted.map((imp, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs min-w-0">
                    <span className="text-amber-500 shrink-0">├─</span>
                    <span className="text-text-muted shrink-0">{imp.type}:</span>
                    <span className="font-medium truncate">{imp.id}</span>
                    <span className="text-text-muted truncate">— {imp.impact}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {actions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-muted mb-1">Recommended Actions</p>
              <div className="space-y-1">
                {actions.map((action, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs min-w-0">
                    <span className={`shrink-0 ${action.type === 'escalate' ? 'text-red-500' : 'text-aqua'}`}>→</span>
                    <span className="truncate">{action.label}</span>
                    {action.type === 'escalate' && <span className="text-[10px] text-red-500 font-medium shrink-0">ESCALATE</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-nn-border">
            {alert.status === 'active' && (
              <button onClick={() => onAcknowledge(alertId)} className="flex items-center gap-1 text-xs text-text-muted hover:text-text px-2 py-1 rounded hover:bg-surface-warm transition-colors">
                <Eye size={ICON_PX.sm} /> Acknowledge
              </button>
            )}
            <button onClick={() => onResolve(alertId)} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-500 px-2 py-1 rounded hover:bg-surface-warm transition-colors">
              <CheckCircle2 size={ICON_PX.sm} /> Resolve
            </button>
            <button onClick={() => onDismiss(alertId)} className="flex items-center gap-1 text-xs text-text-muted hover:text-text px-2 py-1 rounded hover:bg-surface-warm transition-colors">
              <X size={ICON_PX.sm} /> Dismiss
            </button>
            <div className="flex-1" />
            <button
              onClick={() => onTellMeMore(alert)}
              className="flex items-center gap-1 text-xs text-aqua hover:text-aqua/80 px-2 py-1 rounded hover:bg-surface-warm transition-colors"
            >
              <MessageSquare size={ICON_PX.sm} /> Tell me more
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── Build formatted alert context as markdown (first message in chat) ────────

function buildAlertMarkdown(alert) {
  const sev = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.medium;
  const parts = [];
  parts.push(`**${sev.label}** · ${alert.triggerEventType}${alert.confidence != null ? ` · conf: ${Math.round(alert.confidence * 100)}%` : ''}`);
  parts.push(`\n### ${alert.summary}`);
  if (alert.why) {
    parts.push(`\n**Why:** ${alert.why}`);
  }
  if (alert.impacted?.length > 0) {
    parts.push('\n**Impact:**');
    for (const imp of alert.impacted) {
      parts.push(`- **${imp.type}:** ${imp.id} — ${imp.impact}`);
    }
  }
  if (alert.actions?.length > 0) {
    parts.push('\n**Recommended Actions:**');
    for (const action of alert.actions) {
      const tag = action.type === 'escalate' ? ' ⚠️ ESCALATE' : '';
      parts.push(`- → ${action.label}${tag}`);
    }
  }
  return parts.join('\n');
}

// ─── Maximized Chat View (replaces alert list) ───────────────────────────────

function MaximizedChatView({ alert, onClose }) {
  const sev = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.medium;
  const alertId = alert.id ?? alert.alertId;

  // Build first message from alert context
  const initialMessage = {
    role: 'assistant',
    content: buildAlertMarkdown(alert),
    timestamp: new Date().toISOString(),
  };

  // Escape key closes
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header bar */}
      <div className="flex items-center gap-3 pb-3 border-b border-nn-border shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-1 text-xs text-text-muted hover:text-text px-2 py-1 rounded hover:bg-surface-warm transition-colors"
        >
          <ArrowLeft size={ICON_PX.sm} /> Back to alerts
        </button>
        <span className={`w-2 h-2 rounded-full shrink-0 ${sev.dot}`} />
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${sev.badge}`}>{sev.label}</span>
        <span className="text-sm font-medium truncate flex-1 min-w-0">{alert.summary}</span>
        <button
          onClick={onClose}
          className="text-text-muted hover:text-text p-1 rounded hover:bg-surface-warm transition-colors shrink-0"
          title="Close (Esc)"
        >
          <X size={ICON_PX.md} />
        </button>
      </div>

      {/* Chat panel takes remaining space */}
      <div className="flex-1 min-h-0 pt-3">
        <AlertAgentChat
          alertId={alertId}
          alertSummary={alert.summary}
          initialMessages={[initialMessage]}
          maximized
          onClose={onClose}
        />
      </div>
    </div>
  );
}

// ─── AlertsPage (root) ───────────────────────────────────────────────────────

export default function AlertsPage() {
  const { alerts, loading, acknowledge, resolve, dismiss } = useAlerts();
  const [searchParams] = useSearchParams();
  const expandAlertId = searchParams.get('expand');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(expandAlertId ?? null);
  const [chatAlert, setChatAlert] = useState(null);

  // Single-open accordion toggle
  const handleToggle = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleTellMeMore = useCallback((alert) => {
    setChatAlert(alert);
  }, []);

  const filtered = severityFilter === 'all'
    ? alerts
    : alerts.filter((a) => a.severity === severityFilter);

  // Maximized chat view replaces the alert list
  if (chatAlert) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <MaximizedChatView
          alert={chatAlert}
          onClose={() => setChatAlert(null)}
        />
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionHeading info="Operational alerts detected by the intelligence layer. Each alert includes root cause analysis, impact tree, and recommended actions.">
        Operational Alerts
      </SectionHeading>

      <div className="flex gap-2 flex-wrap">
        {['all', 'critical', 'high', 'medium', 'low'].map((sev) => (
          <button
            key={sev}
            onClick={() => setSeverityFilter(sev)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              severityFilter === sev
                ? 'bg-aqua/10 border-aqua text-aqua'
                : 'border-nn-border text-text-muted hover:text-text hover:border-slate'
            }`}
          >
            {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
            {sev !== 'all' && ` (${alerts.filter((a) => a.severity === sev).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-text-muted">Loading alerts...</p>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 size={32} className="text-green-500" />}
          title="No active alerts"
          body="All clear — no operational issues detected."
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((alert) => {
            const id = alert.id ?? alert.alertId;
            return (
              <AlertCard
                key={id}
                alert={alert}
                expanded={expandedId === id}
                highlighted={expandAlertId === id}
                onToggle={handleToggle}
                onAcknowledge={acknowledge}
                onResolve={resolve}
                onDismiss={dismiss}
                onTellMeMore={handleTellMeMore}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
